import React, { useState, useEffect, memo, useRef, useCallback } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
var relativeTime = require('dayjs/plugin/relativeTime')
dayjs.extend(relativeTime)
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { Formik, Field } from 'formik'
import * as UpChunk from '@mux/upchunk'
import { colors, stringPadLeft, formatSeconds } from '../../ui/helpers'
import { H1, H2, H3, H6, Text, Span, GradientFont, Error, Label } from '../../ui/Typography'
import { Flex, Box, Container } from '../../ui/Layout'
import { Input, TextArea } from '../../ui/Input'
import Image from '../../ui/Image'
import Icon from '../../ui/Icon'
import CodeCopy from '../../ui/CodeCopy'
import VideoPreview from '../../ui/VideoPreview'
import WidgetPreview from '../../Components/WidgetPreview'
import Button from '../../ui/Button'
import VideoUpload from '../../Videos/VideoUpload'
import { BigDarkSpinner } from '../../ui/Spinner'
import Toggle from '../../ui/Toggle'
import Select from '../../ui/Select'
import ColorPicker from '../../ui/ColorPicker'
import formikFormWrapper from '../../formikFormWrapper'
import { toast } from 'react-toastify'
import { Tabs } from '../CampaignEdit'
import { useVideos } from '../../VideoProvider'
import ProcessingPreview from '../../Components/ProcessingPreview'
import ModalContainer from '../../ui/ModalContainer'
import { ReactSortable } from 'react-sortablejs'
import ReactTooltip from 'react-tooltip'
import Tooltip from '../../ui/ClubbTooltip'
import { uniq, difference, intersection } from 'ramda'
import { getAllowedVideoDuration, getNumberOfAllowedVideos, getPlanName, hasHitVideoLimit, isFreePlan } from '../../Plans'

const FormInput = formikFormWrapper(Input)

import api from '../../api'
import { useAuth } from '../../Auth'

import {
  STEPS,
  CAMPAIGN_RULE_TYPES,
  CAMPAIGN_STATUS,
  campaignRuleTypesOptions,
  WIDGET_POSITIONS,
  widgetPositionOptions
} from '../types'
import ImageUpload from '../../Images/ImageUpload'
import ToggleRedGreen from '../../ui/ToggleRedGreen'
import { UpgradeContainer } from '../../Videos/index'
import Popover from '../../ui/Popover'
import { FilterButton, FullWidthForm, InputComponent, InputContainer } from '../../Videos/VideoSearch'

const ImageComp = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  background-position: center;
`

const Grid = styled(Flex)`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: 1fr 1fr 1fr;
  @media (min-width: 1500px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (min-width: 1800px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (min-width: 2200px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  }
  @media (min-width: 2500px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  }
`

const VideoPill = styled(Flex)`
  box-shadow: 0px 1px 14px rgba(50, 50, 71, 0.08), 0px 0px 1px rgba(50, 50, 71, 0.2);
  margin-bottom: 16px;
  padding: 10px;
  border-radius: 4px;
  background-color: white;
  flex-direction: column;
  word-break: break-all;
`

const GrabIcon = styled(Icon)`
  cursor: grab;
`

const VerticalDivider = styled(Box)`
  border-right: 1px solid rgba(166, 183, 212, 1);
  width: 0px;
  margin-right: 16px;
  margin-left: 8px;
  height: 40px;
`

const Gradient = styled(Flex)`
  background: rgb(255, 255, 255);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 80%, rgba(0, 0, 0, 0.3) 100%);
`

const ScrollButton = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(270deg, white 0%, white 50%, rgba(255, 255, 255, 0) 100%);
  z-index: 1;
  cursor: pointer;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  ${(props) =>
    props.direction === 'left' &&
    `
    transform: rotate(180deg);
  `}

  ${(props) => (props.direction === 'left' ? `left: -22px;` : `right: -22px;`)}
  &:hover {
    background: white;
    border-radius: 8px;
    width: 36px;
    ${(props) => (props.direction === 'left' ? `left: -10px;` : `right: -10px;`)}
  }
`

const PAGESIZE = 40

const VideoPills = ({ videosOrImages, setFieldValue }) => {
  let { user } = useAuth()

  ReactTooltip.rebuild()

  return (
    <>
      <Flex flexDirection="column" mb="32px">
        <ReactSortable
          list={videosOrImages}
          setList={(newVideos) => {
            setFieldValue('videoIds', uniq(newVideos.map((video) => video.id)))
          }}
        >
          {videosOrImages.map((videoOrImage, index) => {
            const isVideo = videoOrImage.type === 'VIDEO'
            return (
              <VideoPill key={videoOrImage.id} alignItems="center">
                <Flex alignItems="center">
                  <GrabIcon mr="8px" icon="drag-circles-grey" width="24px" height="24px" alt="drag icon" />
                  {isVideo ? (
                    <>
                      {videoOrImage.playbackId ? (
                        <Image
                          height="60px"
                          width="60px"
                          borderRadius="4px"
                          src={
                            videoOrImage.customThumbnail
                              ? videoOrImage.customThumbnail.includes('clipara.b-cdn.net')
                                ? `${videoOrImage.customThumbnail}?width=60&height=60`
                                : videoOrImage.customThumbnail + `-/scale_crop/${60}x${60}/center/`
                              : `https://image.mux.com/${videoOrImage.playbackId}/thumbnail.jpg?width=60&height=60&fit_mode=smartcrop&time=${videoOrImage.thumbnailTime}`
                          }
                        />
                      ) : (
                        <Box minWidth="60px" width="60px" height="60px">
                          <ProcessingPreview small />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Image
                      height="60px"
                      width="60px"
                      minWidth="60px"
                      borderRadius="4px"
                      objectFit="cover"
                      src={`https://clipara.b-cdn.net/${videoOrImage.path}?width=200`}
                    />
                  )}
                  <Flex flexDirection="column" justifyContent="center" px="10px">
                    <Text fontWeight="600">
                      {videoOrImage.name && videoOrImage.name.length > 45
                        ? videoOrImage.name.substring(0, 45) + '...'
                        : videoOrImage.name}
                    </Text>
                    {isVideo ? (
                      <Text>
                        {stringPadLeft(Math.floor(videoOrImage.duration / 60), '0', 2)}:
                        {stringPadLeft(Math.round(videoOrImage.duration - Math.floor(videoOrImage.duration / 60) * 60), '0', 2)}
                      </Text>
                    ) : null}
                  </Flex>
                  {videosOrImages.length > 1 ? (
                    <Icon
                      cursor="pointer"
                      width="16px"
                      height="16px"
                      icon="bin-grey"
                      onClick={() => {
                        setFieldValue('videoIds', uniq(videosOrImages.filter((v) => v.id !== videoOrImage.id).map((v) => v.id)))
                      }}
                    />
                  ) : null}
                </Flex>
              </VideoPill>
            )
          })}
        </ReactSortable>
      </Flex>
    </>
  )
}

const LineClamp = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 600;
  font-size: 14px;
  line-height: 21.78px;
  word-break: break-all;
  padding-left: 8px;
  padding-right: 8px;
`

const VideoDisplay = memo(
  ({ videoIds, videosToShow, setFieldValue, isVideosTab, page, lastVideoElementRef }) => {
    return videosToShow.slice(0, page * PAGESIZE).map((videoOrImage, index) => {
      const isLastElement = index === videosToShow.slice(0, page * PAGESIZE).length - 1
      const onClick = () => {
        if (videoIds && videoIds.includes(videoOrImage.id)) {
          setFieldValue('videoIds', uniq(videoIds.filter((id) => id !== videoOrImage.id)))
        } else {
          setFieldValue('videoIds', uniq([videoOrImage.id, ...videoIds]))
        }
      }
      return (
        <Container
          ref={isLastElement ? lastVideoElementRef : null}
          border={videoIds.includes(videoOrImage.id) ? `4px solid rgba(120, 149, 255, 1)` : 'transparent'}
          onClick={onClick}
          hover
          p="0px"
          key={videoOrImage.id}
          width="100%"
          height="350px"
          justifyContent="space-between"
          position="relative"
          overflowX="hidden"
        >
          <Flex overflow="hidden" height="100%" position="relative">
            {isVideosTab ? (
              <>
                {videoOrImage.playbackId ? (
                  <ImageComp
                    loading="lazy"
                    width="100%"
                    height="100%"
                    src={
                      videoOrImage.customThumbnail
                        ? videoOrImage.customThumbnail.includes('clipara.b-cdn.net')
                          ? `${videoOrImage.customThumbnail}?width=400&height=400`
                          : videoOrImage.customThumbnail + `-/scale_crop/400x400/center/`
                        : `https://image.mux.com/${videoOrImage.playbackId}/thumbnail.png?time=${videoOrImage.thumbnailTime}&width=400&height=400&fit_mode=crop`
                    }
                  />
                ) : (
                  <ProcessingPreview />
                )}
              </>
            ) : (
              <ImageComp
                loading="lazy"
                width="100%"
                height="100%"
                src={`https://clipara.b-cdn.net/${videoOrImage.path}?width=400`}
              />
            )}
            <Gradient position="absolute" top="0px" left="0px" height="100%" />

            <Box position="absolute" bottom="8px" right="8px">
              <Text color="white" fontSize="12px" fontWeight="600">
                {videoOrImage.duration && formatSeconds(videoOrImage.duration)}
              </Text>
            </Box>
            <Box position="absolute" bottom="8px" left="8px">
              <Text color="white" fontSize="12px" fontWeight="600">
                {dayjs(videoOrImage.createdAt).format('DD MMM')}
              </Text>
            </Box>
          </Flex>
          <Flex flexDirection="column" py="8px" pb="16px" minHeight="100px" height="100px" justifyContent="space-between">
            <LineClamp>{videoOrImage.name}</LineClamp>
            <Flex overflowX="scroll" pl="8px" noScrollBar>
              {(videoOrImage.tagsArray || []).map((tag) => {
                return (
                  <Flex
                    alignItems="center"
                    bg="rgba(225, 232, 255, 1)"
                    width="auto"
                    height="28px"
                    mr="8px"
                    borderRadius="14px"
                    px="16px"
                    key={tag}
                    cursor="pointer"
                  >
                    <Text whiteSpace="nowrap" mb="2px" light fontWeight="500">
                      {tag}
                    </Text>
                  </Flex>
                )
              })}
            </Flex>
          </Flex>
        </Container>
      )
    })
  },
  (prevProps, nextProps) => {
    return (
      prevProps.videoIds === nextProps.videoIds &&
      prevProps.videosToShow === nextProps.videosToShow &&
      prevProps.page === nextProps.page &&
      prevProps.isVideosTab === nextProps.isVideosTab
    )
  }
)

const TABS = {
  VIDEOS: 'VIDEOS',
  IMAGES: 'IMAGES'
}

const TabText = styled(Text)`
  ${(props) => !props.isActive && `color: ${colors.textLight};`}
  padding-bottom: 20px;
  ${(props) => props.isActive && 'border-bottom: 1px solid black;'}
  font-weight: 600;
  margin-right: 30px;
  cursor: pointer;
`

const Tab = ({ onClick, title, isActive }) => {
  return (
    <TabText onClick={onClick} isActive={isActive}>
      {title}
    </TabText>
  )
}

const SelectVideo = ({ setFieldValue, values, selectedVideos, isCreate, submit, setStep }) => {
  const { videos, images, setVideos, refresh } = useVideos()
  let { user } = useAuth()
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const backPage = searchParams.get('backPage')

  const [manuallyAddVideo, setManuallyAddVideo] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  const [videosToShow, setVideosToShow] = useState(videos)
  const [activeTab, setActiveTabState] = useState(TABS.VIDEOS)
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const [page, setPage] = useState(1)
  const observer = useRef()

  const lastVideoElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && videosToShow.length > page * PAGESIZE) {
          setPage((prevPage) => {
            return prevPage + 1
          })
        }
      })
      if (node) observer.current.observe(node)
    },
    [videosToShow, page]
  )

  // Reset page when search or filters change
  useEffect(() => {
    setPage(1)
  }, [search, selectedTags, activeTab])

  const setActiveTab = (x) => {
    setActiveTabState(x)
    setManuallyAddVideo(false)
    setVideosToShow([])
  }
  const isVideosTab = activeTab === TABS.VIDEOS

  const videosOrImages = isVideosTab ? videos : images

  let inputAllTags = []
  videosOrImages.forEach((v) => (inputAllTags = inputAllTags.concat(v.tagsArray)))
  inputAllTags = uniq(inputAllTags).filter((el) => el)

  const totalExistingDuration =
    videos && videos.length
      ? Math.round(
          videos.reduce((acc, b) => {
            return acc + b.duration
          }, 0)
        )
      : 0
  const overLimit = getAllowedVideoDuration(user) ? getAllowedVideoDuration(user) <= totalExistingDuration : false

  const asyncFunc = async () => {
    refresh()
  }
  useEffect(() => {
    asyncFunc()
  }, [])

  useEffect(() => {
    setVideosToShow(
      search
        ? videosOrImages
            .filter((el) => el.name && el.name.toLowerCase().includes(search.toLowerCase()))
            .filter((v) => {
              if (!selectedTags || !selectedTags.length) {
                return true
              } else {
                return !!intersection(v.tagsArray || [], selectedTags).length
              }
            })
        : videosOrImages.filter((v) => {
            if (!selectedTags || !selectedTags.length) {
              return true
            } else {
              return !!intersection(v.tagsArray || [], selectedTags).length
            }
          })
    )
  }, [search, videos, images, selectedTags, activeTab, manuallyAddVideo])

  const tagsContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const checkScroll = () => {
    if (tagsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tagsContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
    }
  }

  useEffect(() => {
    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [inputAllTags])

  const scroll = (direction) => {
    if (tagsContainerRef.current) {
      const scrollAmount = 400 // Adjust this value to change scroll distance
      tagsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  console.log(values, '<<<<')

  const videoResultsRef = useRef(null)

  useEffect(() => {
    if (videoResultsRef.current) {
      videoResultsRef.current.scrollTop = 0
    }
  }, [search, selectedTags, activeTab])

  return (
    <Flex height="100vh">
      <Flex
        minWidth="400px"
        width="400px"
        flexDirection="column"
        pt="20px"
        borderRight="1px solid #A6B7D4;"
        height="100%"
        overflowY="scroll"
      >
        <Flex
          mb="16px"
          onClick={() => {
            if (searchParams.get('homeback')) {
              return navigate('/')
            }
            if (backPage) {
              return navigate(backPage)
            }

            isCreate
              ? user.shopifyShopUrl
                ? setStep(STEPS.SHOPPABLE_SELECT)
                : setStep(STEPS.ON_SITE_OFF_SITE)
              : navigate(`/campaigns/editor/${values.id}`)
          }}
          cursor="pointer"
          width="auto"
          px="32px"
        >
          <Icon mt="4px" mr="16px" icon="chevron-left-lightlight" height="16px" width="16px" />
          <Text lightLight fontSize="14px" textAlign="left">
            {isCreate ? 'Back' : 'Cancel'}
          </Text>
        </Flex>

        <Flex
          borderBottom="1px solid rgba(237, 242, 247, 1)"
          borderTop="1px solid rgba(237, 242, 247, 1)"
          pt="16px"
          pb="16px"
          mb="16px"
          px="32px"
          flexDirection="column"
        >
          <Flex alignItems="center" mb="4px" justifyContent="space-between">
            <Label mb="0px" mr="16px">
              Randomise Order
            </Label>
            <ToggleRedGreen
              setSelected={(selected) => {
                setFieldValue('randomContentOrder', selected)
              }}
              value1={false}
              value2={true}
              selected={values.randomContentOrder}
            />
          </Flex>
          <Text light fontWeight="400" fontSize="11px">
            Content will change order every load
          </Text>
        </Flex>
        <Flex px="32px" flexDirection="column">
          <VideoPills isVideosTab={isVideosTab} videosOrImages={selectedVideos} setFieldValue={setFieldValue} values={values} />
        </Flex>
      </Flex>
      <Flex flexDirection="column" height="100%" width="calc(100% - 400px)">
        {videos ? (
          <>
            <Flex px="24px" mb="24px" flexDirection="column" borderBottom="1px solid rgba(237, 242, 247, 1)">
              <Flex mb="16px" height="60px" minHeight="60px" alignItems="center" justifyContent="space-between">
                <Text fontSize="20px" fontWeight="600">
                  Select content to add to your campaign
                </Text>
                <Flex width="auto" alignItems="center">
                  {values.videoIds && values.videoIds.length ? (
                    <Text bold mr="16px" light fontWeight="500">
                      ({values.videoIds.length} selected)
                    </Text>
                  ) : null}
                  <Tooltip
                    direction="bottom"
                    label={!values.videoIds || !values.videoIds.length ? 'You must select at least one video' : undefined}
                  >
                    <Button
                      isDisabled={!values.videoIds || !values.videoIds.length}
                      variant="black"
                      label={isCreate ? 'Create Campaign' : 'Save'}
                      onClick={async () => {
                        if (isCreate && !values.isShareCampaign) {
                          await submit(STEPS.DISPLAY_TYPE)
                          return
                        }
                        await submit()
                        if (values.isDownloadCampaign) {
                          return setStep(STEPS.WIDGET_TYPE)
                        }
                        // Share has some logic in submit for this
                        if (!(values.isShareCampaign && isCreate)) {
                          navigate(`/campaigns/editor/${values.id}`)
                        }
                      }}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
              <Flex position="relative" width="auto">
                <Flex>
                  <Tab onClick={() => setActiveTab(TABS.VIDEOS)} title="Videos" isActive={activeTab === TABS.VIDEOS} />
                  <Tab onClick={() => setActiveTab(TABS.IMAGES)} title="Images" isActive={activeTab === TABS.IMAGES} />
                </Flex>
                <InputContainer mr="0px">
                  <Icon icon="search-grey" width={14} height={14} />
                  <FullWidthForm onSubmit={submit}>
                    <InputComponent placeholder="Search ..." onChange={(e) => setSearch(e.target.value)} value={search} />
                  </FullWidthForm>
                  {search ? (
                    <Icon
                      cursor="pointer"
                      icon="circle-cross-grey"
                      width={14}
                      height={14}
                      onClick={() => {
                        setSearch('')
                      }}
                    />
                  ) : null}
                </InputContainer>
              </Flex>
            </Flex>
            {manuallyAddVideo ? (
              isVideosTab ? (
                <Flex flexDirection="column" overflowY="scroll">
                  <VideoUpload
                    closeModal={() => {
                      asyncFunc()
                      setManuallyAddVideo(false)
                    }}
                    isModal
                  />
                  <Box pb="100px"></Box>
                </Flex>
              ) : (
                <Flex flexDirection="column" overflowY="scroll">
                  <ImageUpload
                    closeModal={() => {
                      asyncFunc()
                      setManuallyAddVideo(false)
                    }}
                    isModal
                  />
                  <Box pb="100px"></Box>
                </Flex>
              )
            ) : (
              <>
                {inputAllTags && inputAllTags.length ? (
                  <Flex px="24px">
                    <Popover
                      isOpen={filterPopoverOpen}
                      onClose={() => setFilterPopoverOpen(false)}
                      content={
                        <Box maxHeight="300px" overflowY="auto" width="200px" p="16px" noScrollBar>
                          {inputAllTags.map((tag) => (
                            <Flex
                              key={tag}
                              alignItems="center"
                              p="8px"
                              cursor="pointer"
                              justifyContent="space-between"
                              onClick={() => {
                                const isSelected = selectedTags.includes(tag)
                                if (isSelected) {
                                  setSelectedTags(selectedTags.filter((t) => t !== tag))
                                } else {
                                  setSelectedTags([...selectedTags, tag])
                                }
                              }}
                            >
                              <Text fontWeight="400" fontSize="14px">
                                {tag}
                              </Text>
                              {selectedTags.includes(tag) && <Icon icon="tick-black" width={16} height={16} />}
                            </Flex>
                          ))}
                        </Box>
                      }
                    >
                      <FilterButton mr="24px" onClick={() => setFilterPopoverOpen(!filterPopoverOpen)}>
                        <Text fontWeight="400" fontSize="14px">
                          Filter
                        </Text>
                        <Icon icon="filter-black" width={14} height={14} />
                      </FilterButton>
                    </Popover>
                    <Box position="relative" width="calc(100% - 92px - 16px);">
                      <ScrollButton direction="left" isVisible={canScrollLeft} onClick={() => scroll('left')}>
                        <Icon icon="chevron-right-grey" width={24} height={24} />
                      </ScrollButton>
                      <Flex overflowX="scroll" noScrollBar position="relative" ref={tagsContainerRef} onScroll={checkScroll}>
                        {difference(inputAllTags, selectedTags).map((tag) => {
                          const count = isVideosTab
                            ? videos.filter(
                                (v) =>
                                  v?.tagsArray?.includes(tag) &&
                                  (!search || v?.name?.toLowerCase().includes(search.toLowerCase()))
                              )?.length
                            : images.filter(
                                (i) =>
                                  i?.tagsArray?.includes(tag) &&
                                  (!search || i?.name?.toLowerCase().includes(search.toLowerCase()))
                              )?.length
                          const isSelected = selectedTags.includes(tag)
                          return (
                            <Flex
                              alignItems="center"
                              bg={isSelected ? '#A6B7D4' : 'rgba(225, 232, 255, 1)'}
                              width="auto"
                              height="36px"
                              mr="8px"
                              borderRadius="20px"
                              px="16px"
                              key={tag}
                              position="relative"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedTags(selectedTags.filter((t) => t !== tag))
                                } else {
                                  setSelectedTags(selectedTags.concat(tag))
                                }
                                setPage(1)
                                setSearch('')
                              }}
                              cursor="pointer"
                            >
                              <Text mb="2px" whiteSpace="nowrap" color={isSelected ? 'white' : colors.textLight} fontWeight="500">
                                {tag} ({count})
                              </Text>
                            </Flex>
                          )
                        })}
                      </Flex>

                      <ScrollButton direction="right" isVisible={canScrollRight} onClick={() => scroll('right')}>
                        <Icon icon="chevron-right-grey" width={24} height={24} />
                      </ScrollButton>
                    </Box>
                  </Flex>
                ) : null}
                {selectedTags && selectedTags.length ? (
                  <Flex flexWrap="wrap" mt="16px" px="24px">
                    {selectedTags.map((tag) => {
                      return (
                        <Flex
                          alignItems="center"
                          bg={'#A6B7D4'}
                          width="auto"
                          height="36px"
                          mr="8px"
                          borderRadius="20px"
                          px="16px"
                          key={tag}
                          position="relative"
                          onClick={() => {
                            setSelectedTags(selectedTags.filter((t) => t !== tag))
                            setPage(1)
                            setSearch('')
                          }}
                          cursor="pointer"
                        >
                          <Text mb="2px" whiteSpace="nowrap" color={'white'} fontWeight="500">
                            {tag}
                          </Text>
                          <Icon ml="8px" width="14px" height="14px" icon="circle-cross-white" />
                        </Flex>
                      )
                    })}
                  </Flex>
                ) : null}
                <Flex height="100%" overflowY="scroll" pt="8px" ref={videoResultsRef} mt="16px">
                  <Grid height="100%" px="24px" pb="16px">
                    {/* TWO OF THESE  */}
                    {activeTab !== TABS.IMAGES ? (
                      <>
                        {overLimit ? (
                          <Container alignItems="center" justifyContent="space-between" mr="30px" py="30px" px="24px">
                            <Icon mt="32px" width="32px" height="32px" icon="unlock-purple-gradient" />
                            <Flex flexDirection="column">
                              <Text textAlign="center" fontSize="16px" fontWeight="600" mb="16px" light>
                                You've reached the {getAllowedVideoDuration(user) / 60} minutes limit
                              </Text>
                              <Text textAlign="center" fontSize="14px" light>
                                The {getPlanName(user)} plan has a limit of {getAllowedVideoDuration(user) / 60} minutes of
                                storage. Upgrade to unlock more videos.{' '}
                              </Text>
                            </Flex>
                            <Link to="/account?tab=BILLING">
                              <Button
                                renderLeftIcon={() => <Icon width={11} height={16} icon="lightening-white" />}
                                variant="black"
                                label="Upgrade"
                              />
                            </Link>
                          </Container>
                        ) : (
                          <Container
                            cursor="pointer"
                            alignItems="center"
                            justifyContent="space-between"
                            bg="#E4ECF7"
                            mr="30px"
                            py="16px"
                            px="16px"
                            height="350px"
                            onClick={() => setManuallyAddVideo(true)}
                          >
                            <Flex flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                              <Icon mb="8px" width="20px" height="20px" icon="cross-dark-grey" />
                              <Text fontSize="16px" fontWeight="600" color="#8492A6">
                                Add a video
                              </Text>
                            </Flex>

                            {isFreePlan(user) && user.isContentCreator ? (
                              <Link to="/account?tab=BILLING">
                                <UpgradeContainer>
                                  <Text mr="16px" fontWeight="400" color="white" lineHeight="normal">
                                    Upgrade for higher and original quality uploads and downloads.
                                  </Text>
                                  <Icon icon="lightening-white" height="16px" width="11px" />
                                </UpgradeContainer>
                              </Link>
                            ) : null}
                          </Container>
                        )}
                      </>
                    ) : null}
                    {activeTab === TABS.IMAGES ? (
                      <Container
                        cursor="pointer"
                        alignItems="center"
                        justifyContent="center"
                        bg="#E4ECF7"
                        mr="30px"
                        py="16px"
                        px="16px"
                        height="350px"
                        onClick={() => setManuallyAddVideo(true)}
                      >
                        <Icon mb="8px" width="20px" height="20px" icon="cross-dark-grey" />
                        <Text fontSize="16px" fontWeight="600" color="#8492A6">
                          Upload an image
                        </Text>
                      </Container>
                    ) : null}

                    <VideoDisplay
                      page={page}
                      isVideosTab={isVideosTab}
                      videoIds={values.videoIds}
                      videosToShow={videosToShow}
                      setFieldValue={setFieldValue}
                      lastVideoElementRef={lastVideoElementRef}
                    />
                  </Grid>
                </Flex>
              </>
            )}
          </>
        ) : (
          <BigDarkSpinner />
        )}
        {/* {videos && !videos.length ? (
          <Flex flexDirection="row">
            <Flex
              mb="24px"
              justifyContent="center"
              alignItems="center"
              bg={colors.softBlue}
              width="124px"
              height="124px"
              borderRadius="124px"
            >
              <img width="56px" height="56px" src="/wavingEmoji.png" />
            </Flex>
            <H3 textAlign="center">Upload your first video</H3>
            <Text mb="24px" textAlign="center">
              Looks like you don't have any videos yet. Click here to upload your first video
            </Text>
            <Link to="/videos/upload">
              <Button width="auto" variant="black" label="Create" />
            </Link>
          </Flex>
        ) : null} */}
      </Flex>
    </Flex>
  )
}

export default SelectVideo
